<script setup lang="ts">
interface Props {
  label: string;
  color?: string;
  textColor?: string;
  size?: 'md' | 'lg';
  variant?: 'default' | 'glass' | 'shimmer' | 'shadow' | 'neon' | 'starry';
}

const props = withDefaults(defineProps<Props>(), {
  color: 'primary',
  textColor: 'white',
  size: 'md',
  variant: 'default',
});

// --- Warp Drive (Star Trek intro) ---
import {
  Scene,
  OrthographicCamera,
  WebGLRenderer,
  BufferGeometry,
  ShaderMaterial,
  Points,
  AdditiveBlending,
  CanvasTexture,
  Float32BufferAttribute,
} from 'three';

const starCanvas = ref<HTMLCanvasElement | null>(null);
let rafId: number | null = null;
let renderer: WebGLRenderer | null = null;
let sceneRefs: { geometry: BufferGeometry; material: ShaderMaterial } | null =
  null;
let speedUniform: { value: number } | null = null;
let scene: Scene | null = null;
let camera: OrthographicCamera | null = null;
let warpUniforms: {
  uAccumProgress: { value: number };
  uTexture: { value: CanvasTexture };
  uSpeedMultiplier: { value: number };
  uStarSize: { value: number };
} | null = null;

const BASE_SPEED = 0.1;
const HOVER_SPEED = 0.6;
const STAR_SIZE = 4; // базовый размер звёзд (крути меня)
const SPEED_LERP = 0.3; // резкий разгон

const { starCount, dprCap, antialias } = use3DQuality();

const cameraRef = inject<Ref<{ zoom: number } | null>>('cameraRef', ref(null));

// Текстура линзового блика (glow gradient)
const createGlowTexture = () => {
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.15, 'rgba(255,255,255,0.8)');
  g.addColorStop(0.4, 'rgba(255,255,255,0.2)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  return new CanvasTexture(c);
};

// Вершинный шейдер — звёзды рождаются из центра (корабль) и разлетаются к краям
const warpVertShader = `
  attribute float aAngle;
  attribute float aSize;
  attribute float aSpeed;
  attribute float aSeed;
  uniform float uAccumProgress;
  uniform float uSpeedMultiplier;
  uniform float uStarSize;
  varying float vSpeed;
  varying float vSpeedGlobal;
  varying float vAlpha;
  varying vec2 vScreenPos;

  void main() {
    // Radial progress: каждая звезда циклически летит от центра к краю
    float progress = mod(uAccumProgress * aSpeed + aSeed, 1.0);

    // Позиция: от центра к краю по направлению aAngle (в пиксельных координатах)
    float dist = progress * 300.0;
    float x = cos(aAngle) * dist;
    float y = sin(aAngle) * dist;

    vec3 pos = vec3(x, y, 0.0);

    vec4 mvPos = modelViewMatrix * vec4(pos, 1.0);

    // Размер растёт при удалении от центра (звезда "пролетает мимо")
    gl_PointSize = aSize * uStarSize * (1.0 + progress * 4.0);
    gl_Position = projectionMatrix * mvPos;

    vScreenPos = gl_Position.xy / gl_Position.w;
    vSpeed = aSpeed;
    vSpeedGlobal = uSpeedMultiplier;
    // Fade in у центра, fade out у краёв
    vAlpha = smoothstep(0.0, 0.12, progress) * smoothstep(1.0, 0.75, progress);
  }
`;

// Фрагментный шейдер — хроматическая аберрация + шлейф (спектральное расщепление)
const warpFragShader = `
  uniform sampler2D uTexture;
  varying float vSpeed;
  varying float vSpeedGlobal;
  varying float vAlpha;
  varying vec2 vScreenPos;

  void main() {
    vec2 uv = gl_PointCoord - 0.5;

    // Направление от центра экрана к частице
    vec2 dir = normalize(vScreenPos);
    if (length(vScreenPos) < 0.001) dir = vec2(0.0, 1.0);

    // Растяжение вдоль направления движения — шлейф (чем быстрее — тем длиннее)
    float stretch = 1.0 + vSpeedGlobal * 8.0;
    // Проекция uv на направление и перпендикуляр
    float along = abs(dot(uv, dir));
    float perp = abs(dot(uv, vec2(-dir.y, dir.x)));
    // Эллиптический falloff: вытянут вдоль направления
    float d = sqrt(along * along / (stretch * stretch) + perp * perp);
    if (d > 0.5) discard;

    // Хроматическая аберрация зависит от глобальной скорости корабля
    float sf = vSpeedGlobal * 0.5;

    // RGB-каналы с разным смещением — красный в одну сторону, синий в другую
    float r = texture2D(uTexture, gl_PointCoord - dir * sf * 0.6).r;
    float g = texture2D(uTexture, gl_PointCoord - dir * sf * 0.15).g;
    float b = texture2D(uTexture, gl_PointCoord + dir * sf * 0.7).b;

    vec3 color = vec3(r, g, b);

    // Холодный синий оттенок на краях (Star Trek aesthetic)
    float edge = length(vScreenPos);
    vec3 blueShift = vec3(0.75, 0.85, 1.0);
    color = mix(color, color * blueShift, max(0.0, edge - 0.6) * 0.5);

    gl_FragColor = vec4(color, vAlpha * smoothstep(0.5, 0.0, d));
  }
`;

// Цикл рендера warp-анимации (единый для старта и возобновления после скрытия вкладки)
const animate = () => {
  if (!renderer || !scene || !camera || !warpUniforms) return;
  const dt = 0.016;

  // Target speed из текущего zoom камеры (GSAP-driven)
  let progress = 0;
  if (cameraRef.value) {
    const zoom = cameraRef.value.zoom;
    const zoomMin = 0.5;  // cameraZoomDefault
    const zoomMax = 1.0;  // cameraZoomMax
    progress = Math.max(0, Math.min(1, (zoom - zoomMin) / (zoomMax - zoomMin)));
  }

  const target = BASE_SPEED + (HOVER_SPEED - BASE_SPEED) * progress;
  warpUniforms.uSpeedMultiplier.value +=
    (target - warpUniforms.uSpeedMultiplier.value) * SPEED_LERP;

  // Накопленный прогресс всегда растёт (скорость влияет на темп, но не на направление)
  warpUniforms.uAccumProgress.value += dt * warpUniforms.uSpeedMultiplier.value;

  renderer.render(scene, camera);
  rafId = requestAnimationFrame(animate);
};

const initWarpScene = () => {
  const canvas = starCanvas.value;
  if (!canvas) return;

  const rect = canvas.getBoundingClientRect();
  const w = rect.width;
  const h = rect.height;
  const dpr = Math.min(window.devicePixelRatio, dprCap.value);

  // Scene
  scene = new Scene();

  // Camera — ортографическая, координаты в пикселях canvas
  camera = new OrthographicCamera(-w / 2, w / 2, h / 2, -h / 2, 0.1, 100);
  camera.position.z = 5;

  // Renderer
  renderer = new WebGLRenderer({
    canvas,
    antialias: antialias.value,
    alpha: false,
    preserveDrawingBuffer: true,
  });
  renderer.setSize(w, h);
  renderer.setPixelRatio(dpr);
  renderer.setClearColor(0x050510, 1);

  // Частицы: количество зависит от tier устройства
  const count = starCount.value;
  const pos: number[] = [];
  const angles: number[] = [];
  const speeds: number[] = [];
  const sizes: number[] = [];
  const seeds: number[] = [];

  for (let i = 0; i < count; i++) {
    pos.push(0, 0, 0);
    angles.push(Math.random() * Math.PI * 2); // случайное направление
    speeds.push(0.3 + Math.random() * 0.7);
    sizes.push(0.4 + Math.random() * 0.8); // мелкие — как настоящие звёзды
    seeds.push(Math.random()); // случайная фаза цикла
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(pos, 3)); // требуется THREE.js
  geometry.setAttribute('aAngle', new Float32BufferAttribute(angles, 1));
  geometry.setAttribute('aSpeed', new Float32BufferAttribute(speeds, 1));
  geometry.setAttribute('aSize', new Float32BufferAttribute(sizes, 1));
  geometry.setAttribute('aSeed', new Float32BufferAttribute(seeds, 1));

  const uniforms = {
    uAccumProgress: { value: 0 },
    uTexture: { value: createGlowTexture() },
    uSpeedMultiplier: { value: BASE_SPEED },
    uStarSize: { value: STAR_SIZE },
  };
  speedUniform = uniforms.uSpeedMultiplier;
  warpUniforms = uniforms;

  const material = new ShaderMaterial({
    uniforms,
    vertexShader: warpVertShader,
    fragmentShader: warpFragShader,
    blending: AdditiveBlending,
    depthTest: false,
    transparent: true,
  });

  const points = new Points(geometry, material);
  scene.add(points);

  sceneRefs = { geometry, material };

  // Первый кадр синхронно
  uniforms.uAccumProgress.value += 0.016 * uniforms.uSpeedMultiplier.value;
  renderer.render(scene, camera);
  rafId = requestAnimationFrame(animate);
};

onMounted(() => {
  if (props.variant === 'starry') {
    initWarpScene();
    // Пауза RAF при скрытой вкладке — экономия батареи
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
});

// Пауза/возобновление warp-анимации при переключении вкладок
const onVisibilityChange = () => {
  if (document.hidden) {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  } else if (renderer && rafId === null) {
    // Возобновляем единый цикл рендера
    rafId = requestAnimationFrame(animate);
  }
};

onBeforeUnmount(() => {
  if (rafId !== null) cancelAnimationFrame(rafId);
  document.removeEventListener('visibilitychange', onVisibilityChange);
  if (sceneRefs) {
    sceneRefs.geometry.dispose();
    // Диспоуз glow-текстуры (CanvasTexture в uniforms.uTexture)
    const tex = sceneRefs.material.uniforms.uTexture?.value;
    if (tex?.dispose) tex.dispose();
    sceneRefs.material.dispose();
    sceneRefs = null;
  }
  if (renderer) {
    renderer.dispose();
    renderer = null;
  }
});

const btnClasses = computed(() => {
  const base = [
    'q-btn',
    'q-btn-item',
    'non-selectable',
    'no-outline',
    'q-btn--actionable',
    'q-focusable',
    'q-hoverable',
    'q-btn--no-uppercase',
    'q-btn--active',
    'btn__label',
  ];

  if (props.variant === 'glass') {
    return [...base, 'btn-glass'].join(' ');
  }

  if (props.variant === 'shimmer') {
    return [...base, 'btn-shimmer'].join(' ');
  }

  if (props.variant === 'shadow') {
    return [...base, 'btn-shadow'].join(' ');
  }

  if (props.variant === 'neon') {
    return [...base, 'btn-neon'].join(' ');
  }

  if (props.variant === 'starry') {
    return [...base, 'btn-starry'].join(' ');
  }

  // default
  return [
    ...base,
    'q-btn--unelevated',
    `bg-${props.color}`,
    `text-${props.textColor}`,
    'rounded-4',
  ].join(' ');
});
</script>

<template lang="pug">
button(
  tabindex="0"
  :class="btnClasses"
  style="font-size: 18px; padding: 18px 36px;")
  span.q-focus-helper(v-if="variant === 'default'")
  canvas.btn-starry__canvas(v-if="variant === 'starry'" ref="starCanvas" aria-hidden="true")
  span(:class="variant === 'default' ? 'q-btn__content text-center col items-center q-anchor--skip justify-center row' : 'btn__content'")
    span.block.btn__label {{ label }}
</template>

<style lang="sass">

// < Button label base
.btn__label
  font-family: Inter
  font-weight: 500
  line-height: 19.36px
  text-align: left

.btn__content
  display: flex
  align-items: center
  justify-content: center
  position: relative
  z-index: 1

// > Glassmorphism variant
.btn-glass
  background: rgba(255, 255, 255, 0.12)
  backdrop-filter: blur(16px)
  -webkit-backdrop-filter: blur(16px)
  border: 1px solid rgba(255, 255, 255, 0.25)
  border-radius: 16px
  color: #fff
  transition: all 0.3s ease
  position: relative
  overflow: hidden

  &:hover
    background: rgba(255, 255, 255, 0.2)
    border-color: rgba(255, 255, 255, 0.4)

  &:active
    background: rgba(255, 255, 255, 0.28)
    border-color: rgba(255, 255, 255, 0.5)
    transform: scale(0.98)

// > Shimmer variant (dark glass)
.btn-shimmer
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0.4) 100%)
  backdrop-filter: blur(12px)
  -webkit-backdrop-filter: blur(12px)
  border: 1px solid rgba(255, 255, 255, 0.12)
  border-radius: 16px
  color: #fff
  transition: all 0.3s ease
  position: relative
  overflow: hidden

  &::after
    content: ''
    position: absolute
    top: 0
    left: -100%
    width: 60%
    height: 100%
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.12), transparent)
    animation: btn-shimmer 3s infinite
    pointer-events: none

  &:hover
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.65) 0%, rgba(0, 0, 0, 0.45) 100%)
    border-color: rgba(255, 255, 255, 0.2)

  &:active
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.55) 100%)
    border-color: rgba(255, 255, 255, 0.3)
    transform: scale(0.98)

@keyframes btn-shimmer
  0%
    left: -100%
  100%
    left: 200%

// > Shadow variant
.btn-shadow
  background: #fff
  border: none
  border-radius: 16px
  color: #000
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(180, 100, 40, 0.15)
  transition: all 0.3s ease
  transform: translateY(-2px)

  &:hover
    transform: translateY(-4px)
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35), 0 12px 40px rgba(180, 100, 40, 0.2)

  &:active
    transform: translateY(-1px)
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 4px 16px rgba(180, 100, 40, 0.15)

// > Neon variant
.btn-neon
  background: rgba(255, 255, 255, 0.05)
  backdrop-filter: blur(8px)
  -webkit-backdrop-filter: blur(8px)
  border: 1.5px solid rgba(255, 255, 255, 0.5)
  border-radius: 16px
  color: #fff
  box-shadow: 0 0 12px rgba(255, 255, 255, 0.15), inset 0 0 12px rgba(255, 255, 255, 0.05)
  transition: all 0.3s ease

  &:hover
    border-color: rgba(255, 255, 255, 0.8)
    box-shadow: 0 0 24px rgba(255, 255, 255, 0.3), inset 0 0 16px rgba(255, 255, 255, 0.08)
    background: rgba(255, 255, 255, 0.1)

  &:active
    border-color: rgba(255, 255, 255, 0.9)
    box-shadow: 0 0 32px rgba(255, 255, 255, 0.4), inset 0 0 20px rgba(255, 255, 255, 0.12)
    background: rgba(255, 255, 255, 0.15)

// > Starry variant (warp drive / Star Trek intro)
.btn-starry
  border: 1px solid rgba(255, 255, 255, 0.15)
  border-radius: 16px
  color: #fff
  position: relative
  overflow: hidden
  transition: border-color 0.3s ease
  background: #050510

  &:hover
    border-color: rgba(255, 255, 255, 0.3)

  &:active
    border-color: rgba(255, 255, 255, 0.45)

  .btn-starry__canvas
    position: absolute
    inset: 0
    width: 100%
    height: 100%
    pointer-events: none

// >
</style>
